import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { graphqlClient, useQuery } from 'utils/graphql';

import { TimeTravelContext } from 'components/TimeTravel/context';
import { FundAggregation, User, TagsTagTypeChoices } from 'libs/api/invest/generated/schema';

import { MappedSettings, SettingsType } from '../dashboardSettingsTypes';
import { makeMappedSettings } from '../dashboardSettingsUtils';
import { SETTINGS_MAP } from './constants';

export function useFund() {
  const { id: fundId } = useParams<{ id: string }>();
  const history = useHistory();
  const { date: asOfDate } = React.useContext(TimeTravelContext);

  const [exportMenuAnchor, setExportMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [fundAggregation, setFundAggregation] = React.useState<FundAggregation>();
  const [fundQuarters, setFundQuarters] = React.useState<FundAggregation[]>();
  const [fundLoading, setFundLoading] = React.useState(false);
  const [fundLoadingLP, setFundLoadingLP] = React.useState(false);
  const [mappedSettings, setMappedSettings] = React.useState<MappedSettings>();
  const [user, setUser] = React.useState<User>();

  const funds = useQuery(
    {
      funds: {
        rawCurrency: 1,
        divisions: {
          id: 1,
          name: 1,
          type: 1,
        },
        fundInvestments: {
          company: {
            rawCurrency: 1,
          }
        },
        groups: {
          id: 1,
          name: 1,
          type: 1,
        },
        id: 1,
        isSubfund: 1,
        name: 1,
        subfunds: {
          id: 1,
          name: 1,
        }
      }
    },
    'funds',
  )

  React.useEffect(() => {
    if (fundId) {
      const queryParams = new URLSearchParams(window.location.search);
      loadFund(
        fundId,
        (queryParams.get('aggrType') as TagsTagTypeChoices) || undefined,
        queryParams.get('currency') || undefined,
      );
      graphqlClient.chain.query
        .currentUser()
        .execute({ role: 1 })
        .then(response => {
          setUser(response || undefined);
          if (response?.role === 'LP') {
            graphqlClient.chain.query
              .currentUser({ asOfDate, fundId })
              .execute({
                profileLp: {
                  contributedCapital: 1,
                  nav: 1,
                  returnPercent: 1,
                },
                role: 1, //TODO If current user role is LP
                lpInvestments: {
                  fund: {
                    id: 1
                  }
                }
              })
              .then(response => setUser(response || undefined))
              .finally(() => setFundLoadingLP(false));
          }
        });
    } else {
      graphqlClient.chain.query
        .fundAggregation()
        .execute({ aggregationId: 1 })
        .then(fund => {
          if (!fund) {
            history.push(`/admin/fund`);
            return;
          }
          history.push(`/fund/${fund.aggregationId}`);
          loadFund(fund.aggregationId, undefined).then();
        })
    }
  }, [asOfDate]);

  React.useEffect(() => {
    if (!fundAggregation) return;
    setMappedSettings(makeMappedSettings(
      fundAggregation.dashboardSettings,
      SETTINGS_MAP,
    ));
  }, [fundAggregation])
  
  async function loadFund(
    aggregationId: string,
    aggregationType: TagsTagTypeChoices|undefined,
    currency?: string
  ) {
    setFundLoading(true);
    setFundLoadingLP(true)
    setFundQuarters(undefined);
    graphqlClient.chain.query
      .fundAggregation({ aggregationId, aggregationType, currency, asOfDate })
      .execute({
        __scalar: 1,
        currentLpInvestment: {
          bookValue: 1,
        },
        dashboardSettings: {
          sections: {
            __scalar: 1,
            components: { __scalar: 1 },
          }
        },
        defaultDashboardSettings: {
          sections: {
            __scalar: 1,
            components: { __scalar: 1 },
          }
        },
        funds: {
          name: 1,
          bookValue: 1,
          fundInvestments: {
            capTableUrl: 1,
            company: { id: 1, name: 1 },
            fund: { id: 1, name: 1 },
            ev: 1,
            id: 1,
            investedCapital: 1,
            irr: 1,
            multiple: 1,
            nav: 1,
            ownershipPercent: 1,
            realizedValue: 1,
            status: 1,
          },
          investedCapital: 1,
          id: 1,
          isSubfund: 1,
          parentFund: { id: 1, name: 1 },
          subfunds: {
            bookValue: 1,
            fundInvestments: {
              capTableUrl: 1,
              company: { id: 1, name: 1 },
              ev: 1,
              fund: { id: 1 },
              id: 1,
              investedCapital: 1,
              irr: 1,
              multiple: 1,
              nav: 1,
              ownershipPercent: 1,
              realizedValue: 1,
              status: 1,
            },
            name: 1,
            id: 1,
            investedCapital: 1,
          }
        },
        investmentsBySector: { __scalar: 1 },
      })
      .then(response => {
        setFundAggregation(response || undefined);
        if (response && response.isSingleFund && !response.funds[0].isSubfund) {
          graphqlClient.chain.query
            .fundAggregationQuarters({
              aggregationId: response.aggregationId,
              aggregationType: response.aggregationType,
              currency: response.currency,
              endDate: asOfDate
            })
            .execute({
              asOfDate: 1,
              netIrr: 1,
              multiple: 1,
              dpi: 1,
              rvpi: 1,
            })
            .then(response => setFundQuarters(response));
        }
      })
      .finally(() => setFundLoading(false));
  }

  return {
    exportMenuAnchor,
    fundAggregation,
    isSubfund: fundAggregation?.isSingleFund && fundAggregation.funds[0].isSubfund,
    fundLoading,
    fundLoadingLP,
    fundQuarters,
    funds,
    getAggregationVerboseName: () => {
      if (!fundAggregation?.aggregationType) {
        return 'Fund';
      } else if (fundAggregation.aggregationType === 'FUND_DIVISION') {
        return 'Division';
      } else if (fundAggregation.aggregationType === 'FUND_GROUP') {
        return 'Fund Group';
      } else {
        throw new Error(`Unknown aggregation type: ${fundAggregation.aggregationType}`)
      }
    },
    history,
    mappedSettings,
    reloadFund: loadFund,
    setExportMenuAnchor,
    user,

    onSettingsClose: () => loadFund(fundId, undefined),
    saveDashboardSettings: async (dashboardSettings: SettingsType) => {
      return await graphqlClient.chain.mutation
        .fundDashboardSettingsUpdate({ dashboardSettings: dashboardSettings.sections })
        .execute({ dashboardSettings: { dashboardType: 1 } })
        .catch(err => console.error('Failed to save dashboard settings: ', err));
    },
  }
}